export type PrintJobStatus = 'queued' | 'dispatching' | 'accepted' | 'failed' | 'unknown';
export type PrintTargetStatus = 'pending' | 'dispatching' | 'accepted' | 'failed' | 'unknown';

export interface PrintTargetIntent {
  readonly role: string;
  readonly copies: number;
}

export interface PrintDocumentRef {
  readonly aggregateType: string;
  readonly aggregateId: string;
  readonly revision: number;
}

export interface PrintIntentInput {
  readonly intentId: string;
  readonly orderId?: string;
  readonly orderRevision?: number;
  readonly documentRef?: PrintDocumentRef;
  readonly documentKind: string;
  readonly templateVersion: string;
  readonly payloadVersion: string;
  readonly payload: unknown;
  readonly targets: readonly PrintTargetIntent[];
  readonly requestedBy: string;
  readonly reason?: string;
}

export interface PrintRetryAudit {
  readonly requestedBy: string;
  readonly reason: string;
  readonly at: string;
}

export interface PrintTargetSnapshot extends PrintTargetIntent {
  readonly status: PrintTargetStatus;
  readonly lastError?: string;
}

export interface PrintJobSnapshot extends PrintIntentInput {
  readonly dedupeKey: string;
  readonly status: PrintJobStatus;
  readonly attempts: number;
  readonly targets: readonly PrintTargetSnapshot[];
  readonly reprintOfIntentId?: string;
  readonly retryHistory: readonly PrintRetryAudit[];
}

export interface PrintJobRepository {
  getByDedupeKey(dedupeKey: string): Promise<PrintJobSnapshot | null>;
  save(job: PrintJobSnapshot): Promise<void>;
}

export interface PrinterExecutionRequest {
  readonly executionId: string;
  readonly dedupeKey: string;
  readonly payloadVersion: string;
  readonly templateVersion: string;
  readonly payload: unknown;
  readonly targets: readonly PrintTargetIntent[];
}

export interface PrinterExecutionTargetResult {
  readonly targetRole: string;
  readonly status: 'accepted' | 'failed' | 'unknown';
  readonly errorCode?: string;
}

export interface PrinterExecutionResult {
  readonly executionId: string;
  readonly status: 'accepted' | 'failed' | 'unknown';
  readonly targets: readonly PrinterExecutionTargetResult[];
}

export interface PrinterExecutionPort {
  execute(request: PrinterExecutionRequest): Promise<PrinterExecutionResult>;
}

export interface ReprintRequest {
  readonly intentId: string;
  readonly requestedBy: string;
  readonly reason: string;
}

export interface RetryFailedRequest {
  readonly requestedBy: string;
  readonly reason: string;
  readonly at: string;
}

const clone = <T>(value: T): T => structuredClone(value);

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, stableValue(entry)]),
    );
  }
  return value;
};

const signatureOf = (value: unknown): string => JSON.stringify(stableValue(value));

const assertText = (value: string | undefined, code: string): void => {
  if (!value?.trim()) throw new Error(code);
};

const assertTimestamp = (value: string, code: string): void => {
  if (!Number.isFinite(Date.parse(value))) throw new Error(code);
};

const assertDocumentRef = (documentRef: PrintDocumentRef): void => {
  assertText(documentRef.aggregateType, 'PRINT_DOCUMENT_AGGREGATE_TYPE_REQUIRED');
  assertText(documentRef.aggregateId, 'PRINT_DOCUMENT_AGGREGATE_ID_REQUIRED');
  if (!Number.isSafeInteger(documentRef.revision) || documentRef.revision <= 0) {
    throw new Error('PRINT_DOCUMENT_REVISION_INVALID');
  }
};

const assertIntentIdentity = (input: PrintIntentInput): void => {
  const hasLegacyField = input.orderId !== undefined || input.orderRevision !== undefined;
  const hasDocumentRef = input.documentRef !== undefined;
  if (hasLegacyField && hasDocumentRef) throw new Error('PRINT_IDENTITY_CONFLICT');
  if (!hasLegacyField && !hasDocumentRef) throw new Error('PRINT_IDENTITY_REQUIRED');

  if (hasDocumentRef) {
    assertDocumentRef(input.documentRef as PrintDocumentRef);
    return;
  }

  assertText(input.orderId, 'PRINT_ORDER_ID_REQUIRED');
  if (!Number.isSafeInteger(input.orderRevision) || (input.orderRevision as number) <= 0) {
    throw new Error('PRINT_ORDER_REVISION_INVALID');
  }
};

const assertIntent = (input: PrintIntentInput): void => {
  assertText(input.intentId, 'PRINT_INTENT_ID_REQUIRED');
  assertIntentIdentity(input);
  assertText(input.documentKind, 'PRINT_DOCUMENT_KIND_REQUIRED');
  assertText(input.templateVersion, 'PRINT_TEMPLATE_VERSION_REQUIRED');
  assertText(input.payloadVersion, 'PRINT_PAYLOAD_VERSION_REQUIRED');
  assertText(input.requestedBy, 'PRINT_REQUESTED_BY_REQUIRED');
  if (!Array.isArray(input.targets) || input.targets.length === 0) throw new Error('PRINT_TARGETS_REQUIRED');
  const roles = new Set<string>();
  for (const target of input.targets) {
    assertText(target.role, 'PRINT_TARGET_ROLE_REQUIRED');
    if (roles.has(target.role)) throw new Error('PRINT_TARGET_ROLE_DUPLICATE');
    roles.add(target.role);
    if (!Number.isSafeInteger(target.copies) || target.copies <= 0) throw new Error('PRINT_COPIES_INVALID');
  }
};

const identityKeyFor = (input: PrintIntentInput): string => {
  if (input.documentRef) {
    return `${input.documentRef.aggregateType}:${input.documentRef.aggregateId}:r${input.documentRef.revision}`;
  }
  return `${input.orderId}:r${input.orderRevision}`;
};

const dedupeKeyFor = (input: PrintIntentInput, reprintOfIntentId?: string): string => {
  const identityKey = identityKeyFor(input);
  return reprintOfIntentId
    ? `${identityKey}:${input.documentKind}:${input.payloadVersion}:reprint:${input.intentId}`
    : `${identityKey}:${input.documentKind}:${input.payloadVersion}`;
};

const makeQueued = (input: PrintIntentInput, reprintOfIntentId?: string): PrintJobSnapshot => ({
  ...clone(input),
  dedupeKey: dedupeKeyFor(input, reprintOfIntentId),
  status: 'queued',
  attempts: 0,
  targets: input.targets.map((target) => ({ ...target, status: 'pending' })),
  retryHistory: [],
  ...(reprintOfIntentId ? { reprintOfIntentId } : {}),
});

const projectResult = (job: PrintJobSnapshot): PrinterExecutionResult => ({
  executionId: `print:${job.intentId}:a${Math.max(job.attempts, 1)}`,
  status: job.status === 'accepted' ? 'accepted' : job.status === 'failed' ? 'failed' : 'unknown',
  targets: job.targets.map((target) => ({
    targetRole: target.role,
    status: target.status === 'accepted' ? 'accepted' : target.status === 'failed' ? 'failed' : 'unknown',
    ...(target.lastError ? { errorCode: target.lastError } : {}),
  })),
});

const reconcileExecution = (
  job: PrintJobSnapshot,
  result: PrinterExecutionResult,
): PrintJobSnapshot => {
  const dispatchingRoles = new Set(
    job.targets.filter((target) => target.status === 'dispatching').map((target) => target.role),
  );
  const resultByRole = new Map<string, PrinterExecutionTargetResult>();
  let contractMismatch = false;

  for (const target of result.targets) {
    if (!dispatchingRoles.has(target.targetRole) || resultByRole.has(target.targetRole)) {
      contractMismatch = true;
      continue;
    }
    resultByRole.set(target.targetRole, target);
  }
  if (resultByRole.size !== dispatchingRoles.size) contractMismatch = true;

  const targets: PrintTargetSnapshot[] = job.targets.map((target) => {
    if (target.status !== 'dispatching') return target;
    if (contractMismatch) return { ...target, status: 'unknown', lastError: 'PRINT_EXECUTION_TARGET_MISMATCH' };
    const outcome = resultByRole.get(target.role);
    if (!outcome) return { ...target, status: 'unknown', lastError: 'PRINT_EXECUTION_TARGET_MISSING' };
    return {
      ...target,
      status: outcome.status,
      ...(outcome.errorCode ? { lastError: outcome.errorCode } : {}),
    };
  });

  const anyUnknown = targets.some((target) => target.status === 'unknown' || target.status === 'dispatching');
  const allAccepted = targets.every((target) => target.status === 'accepted');
  const anyFailed = targets.some((target) => target.status === 'failed');
  const status: PrintJobStatus = anyUnknown ? 'unknown' : allAccepted ? 'accepted' : anyFailed ? 'failed' : 'unknown';
  return { ...job, status, targets };
};

export function createPrintAuthority(repository: PrintJobRepository, executionPort: PrinterExecutionPort) {
  const intentsById = new Map<string, PrintJobSnapshot>();
  const signaturesById = new Map<string, string>();
  const idsByDedupe = new Map<string, string>();

  const remember = (input: PrintIntentInput, reprintOfIntentId?: string): PrintJobSnapshot => {
    assertIntent(input);
    const signature = signatureOf({ input, reprintOfIntentId });
    const existingById = intentsById.get(input.intentId);
    if (existingById) {
      if (signaturesById.get(input.intentId) !== signature) throw new Error('PRINT_INTENT_CONFLICT');
      return clone(existingById);
    }

    const queued = makeQueued(input, reprintOfIntentId);
    const existingId = idsByDedupe.get(queued.dedupeKey);
    if (existingId) {
      const existing = intentsById.get(existingId);
      if (!existing) throw new Error('PRINT_INTERNAL_DEDUPE_CORRUPT');
      if (signatureOf({ input: existing, reprintOfIntentId: existing.reprintOfIntentId }) !== signatureOf({ input: queued, reprintOfIntentId })) {
        throw new Error('PRINT_INTENT_CONFLICT');
      }
      return clone(existing);
    }

    intentsById.set(input.intentId, queued);
    signaturesById.set(input.intentId, signature);
    idsByDedupe.set(queued.dedupeKey, input.intentId);
    return clone(queued);
  };

  const getIntent = (intentId: string): PrintJobSnapshot => {
    const intent = intentsById.get(intentId);
    if (!intent) throw new Error('PRINT_INTENT_NOT_FOUND');
    return intent;
  };

  return {
    async listByOrder(orderId: string): Promise<readonly PrintJobSnapshot[]> {
      assertText(orderId, 'PRINT_ORDER_ID_REQUIRED');
      const known = [...intentsById.values()].filter((job) =>
        job.orderId === orderId ||
        (job.documentRef?.aggregateType === 'order' && job.documentRef.aggregateId === orderId),
      );
      const current = await Promise.all(known.map(async (job) =>
        (await repository.getByDedupeKey(job.dedupeKey)) ?? job,
      ));
      return current
        .map((job) => clone(job))
        .sort((left, right) => left.intentId.localeCompare(right.intentId));
    },

    createIntent(input: PrintIntentInput): PrintJobSnapshot {
      return remember(input);
    },

    createReprint(sourceIntentId: string, request: ReprintRequest): PrintJobSnapshot {
      const source = getIntent(sourceIntentId);
      assertText(request.intentId, 'PRINT_INTENT_ID_REQUIRED');
      assertText(request.requestedBy, 'PRINT_REQUESTED_BY_REQUIRED');
      assertText(request.reason, 'PRINT_REPRINT_REASON_REQUIRED');
      const identity = source.documentRef
        ? { documentRef: clone(source.documentRef) }
        : { orderId: source.orderId, orderRevision: source.orderRevision };
      return remember({
        intentId: request.intentId,
        ...identity,
        documentKind: source.documentKind,
        templateVersion: source.templateVersion,
        payloadVersion: `${source.payloadVersion}:reprint:${request.intentId}`,
        payload: clone(source.payload),
        targets: source.targets.map(({ role, copies }) => ({ role, copies })),
        requestedBy: request.requestedBy,
        reason: request.reason,
      }, source.intentId);
    },

    async retryFailed(intentId: string, request: RetryFailedRequest): Promise<PrintJobSnapshot> {
      assertText(request.requestedBy, 'PRINT_RETRY_REQUESTED_BY_REQUIRED');
      assertText(request.reason, 'PRINT_RETRY_REASON_REQUIRED');
      assertTimestamp(request.at, 'PRINT_RETRY_TIMESTAMP_INVALID');
      const intended = getIntent(intentId);
      const job = await repository.getByDedupeKey(intended.dedupeKey);
      if (!job) throw new Error('PRINT_JOB_NOT_PERSISTED');
      if (job.status !== 'failed') throw new Error('PRINT_JOB_NOT_RETRYABLE');
      const queued: PrintJobSnapshot = {
        ...job,
        status: 'queued',
        targets: job.targets.map((target) =>
          target.status === 'failed' ? { ...target, status: 'pending' as const, lastError: undefined } : target,
        ),
        retryHistory: [...job.retryHistory, { ...request }],
      };
      await repository.save(queued);
      return clone(queued);
    },

    async dispatch(intentId: string): Promise<PrinterExecutionResult> {
      const intended = getIntent(intentId);
      let job = await repository.getByDedupeKey(intended.dedupeKey);
      if (!job) {
        job = clone(intended);
        await repository.save(job);
      }

      if (job.status === 'dispatching' || job.status === 'unknown') {
        return projectResult({
          ...job,
          status: 'unknown',
          targets: job.targets.map((target) =>
            target.status === 'dispatching' ? { ...target, status: 'unknown' as const } : target,
          ),
        });
      }
      if (job.status === 'accepted' || job.status === 'failed') return projectResult(job);
      if (job.status !== 'queued') throw new Error('PRINT_JOB_NOT_DISPATCHABLE');

      const dispatching: PrintJobSnapshot = {
        ...job,
        status: 'dispatching',
        attempts: job.attempts + 1,
        targets: job.targets.map((target) =>
          target.status === 'pending' ? { ...target, status: 'dispatching' as const } : target,
        ),
      };
      await repository.save(dispatching);

      const targetsToExecute = dispatching.targets
        .filter((target) => target.status === 'dispatching')
        .map(({ role, copies }) => ({ role, copies }));
      if (targetsToExecute.length === 0) throw new Error('PRINT_NO_PENDING_TARGETS');

      const executionId = `print:${dispatching.intentId}:a${dispatching.attempts}`;
      let result: PrinterExecutionResult;
      try {
        result = await executionPort.execute({
          executionId,
          dedupeKey: dispatching.dedupeKey,
          payloadVersion: dispatching.payloadVersion,
          templateVersion: dispatching.templateVersion,
          payload: clone(dispatching.payload),
          targets: targetsToExecute,
        });
      } catch {
        result = {
          executionId,
          status: 'failed',
          targets: targetsToExecute.map((target) => ({
            targetRole: target.role,
            status: 'failed',
            errorCode: 'PRINT_EXECUTION_EXCEPTION',
          })),
        };
      }
      if (result.executionId !== executionId) throw new Error('PRINT_EXECUTION_ID_MISMATCH');

      const completed = reconcileExecution(dispatching, result);
      await repository.save(completed);
      return projectResult(completed);
    },
  };
}
